import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardBody, Badge, Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Input, Nav, NavItem, NavLink } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { ListChecks, History } from 'lucide-react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import api from '../../helpers/api';
import usePermission from '../../Hooks/usePermission';
import { ASSET_TRANSFER } from '../../constants/permissions';
import { formatCreatedDate } from '../../constants/utils';
import AssetTransferModal from '../AssetTransferModal';
import AssetModal from '../AssetModal';
import TransferDetailsModal from './TransferDetailsModal';
import LocationModal from '../LocationModal';

/**
 * The "Transfers" tab of the Planning page, split into two sub-tabs:
 *  - Requests: pending asset-transfer requests an admin can approve/reject
 *  - History:  the log of executed/decided transfers
 * plus the (bulk) Transfer action. Request IDs, assets and locations are clickable,
 * opening the same modals used elsewhere in the app.
 *
 * Transfers into or out of another organization appear here too, split by a direction filter:
 * OUTGOING are this organization's own requests, INCOMING are assets another organization wants to
 * hand over. Only the side a request is currently waiting on may approve or reject it, so the row
 * actions follow the request's approval stage rather than simply "am I an admin".
 */
const statusBadgeColor = (status) => ({
  PENDING: 'warning',
  REJECTED: 'danger',
  PERMANENT_TRANSFERRED: 'info',
  TEMPORARY_TRANSFERRED: 'primary',
}[status] || 'secondary');

const TransfersTab = () => {
  const { t } = useTranslation();
  const { isAdmin, hasPermission } = usePermission();
  const canTransfer = hasPermission(ASSET_TRANSFER);
  const user = JSON.parse(localStorage.getItem('authUser') || '{}');
  const organizationId = user?.activeOrganizationId ?? null;

  const [activeSubTab, setActiveSubTab] = useState('requests');
  // all | outgoing | incoming — server-side, so an organization's incoming queue is never a
  // client-side guess about someone else's request.
  const [direction, setDirection] = useState('all');
  // One-shot latch for the ?request= deep link.
  const deepLinkHandled = useRef(false);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [deciding, setDeciding] = useState(null); // equipmentId being decided
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, reason: '' });

  // Clickable-link modals (same components the rest of the app uses)
  const [assetModal, setAssetModal] = useState({ isOpen: false, userData: null, loading: false });
  const [equipmentDetails, setEquipmentDetails] = useState({ isOpen: false, equipment: null });
  const [organization, setOrganization] = useState(null); // org object incl. locations, for LocationModal
  const [locationModal, setLocationModal] = useState({
    isOpen: false, title: '', mainData: null, relatedData: [], loading: false, locationData: null,
  });

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('equipment/getAllEquipmentRequests', {
        params: {
          requestType: 'TRANSFER',
          count: 500,
          organizationId,
          ...(direction === 'all' ? {} : { direction: direction.toUpperCase() }),
        },
      });
      setTransfers(data?.response || []);
    } catch (e) {
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, direction]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  // Deep link from a transfer notification: ?request=<equipmentId> opens that request's details
  // once the list has loaded, so the alert lands the admin on the thing it is about.
  // Latched: the list is refetched after every approval and filter change, and without this the
  // modal would spring open again each time long after the user closed it.
  useEffect(() => {
    if (deepLinkHandled.current || transfers.length === 0) return;
    const requestId = new URLSearchParams(window.location.search).get('request');
    if (!requestId) return;
    deepLinkHandled.current = true;
    const match = transfers.find((tr) => String(tr.id) === String(requestId));
    if (match) {
      setEquipmentDetails({ isOpen: true, equipment: match });
      setActiveSubTab(match.status === 'PENDING' ? 'requests' : 'history');
    }
    // Drop the parameter so a later refresh does not re-open it.
    const url = new URL(window.location.href);
    url.searchParams.delete('request');
    window.history.replaceState({}, '', url);
  }, [transfers]);

  // Organization + its locations, needed to resolve a clicked location name to its record.
  useEffect(() => {
    if (!organizationId) return;
    api
      .get(`organization-location/get-by-organization-id?organizationId=${organizationId}&count=9999&hierarchy=false`)
      .then(({ data }) => setOrganization(data?.response?.[0] || null))
      .catch(() => setOrganization(null));
  }, [organizationId]);

  // Is this request addressed TO us (someone else's asset arriving) or one of ours leaving?
  const isIncoming = (tr) => tr.crossOrg && String(tr.destinationOrganization?.id) === String(organizationId);

  // Only the organization the request is currently waiting on may decide it. The server enforces
  // this too; showing the buttons to the wrong side would just produce an error on click.
  const canDecide = (tr) => {
    if (!isAdmin || tr.status !== 'PENDING') return false;
    if (!tr.crossOrg) return true;
    return tr.approvalStage === 'DESTINATION'
      ? isIncoming(tr)
      : String(tr.sourceOrganization?.id) === String(organizationId);
  };

  // The requesting side can always take an offer back while it is still waiting.
  const canWithdraw = (tr) =>
    tr.status === 'PENDING'
    && String(tr.sourceOrganization?.id ?? organizationId) === String(organizationId)
    && (isAdmin || String(tr.requestedBy?.id) === String(user?.id));

  const stageLabel = (tr) => {
    if (!tr.crossOrg || tr.status !== 'PENDING') return null;
    return tr.approvalStage === 'DESTINATION'
      ? t('business.planning.awaiting_destination_approval', 'Awaiting destination approval')
      : t('business.planning.awaiting_source_approval', 'Awaiting source approval');
  };

  const hasCrossOrgRows = transfers.some((tr) => tr.crossOrg);
  const pending = transfers.filter((tr) => tr.status === 'PENDING');
  // Admins decide; a Full User who raised a transfer can still withdraw it, so the column has to
  // appear for them too — otherwise their only way out is the details modal.
  const showActionsColumn = isAdmin || pending.some(canWithdraw);
  const log = transfers.filter((tr) => tr.status !== 'PENDING');

  // ---- Clickable cells (mirroring Equipment.jsx / Assets.jsx patterns) ----

  const handleAssetClick = async (assetId) => {
    if (!assetId) return;
    try {
      setAssetModal((prev) => ({ ...prev, loading: true, isOpen: true }));
      const { data } = await api.get(`asset/get-by-id?id=${assetId}`);
      setAssetModal({ isOpen: true, userData: data?.response, loading: false });
    } catch (e) {
      setAssetModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleLocationClick = async (locationName) => {
    if (!locationName) return;
    // Resolve the clicked name to its location/sublocation record for the modal's info tab.
    let locationData = null;
    const found = organization?.locations?.find(
      (loc) => loc.locationName === locationName
        || loc.subLocations?.some((sub) => sub.locationName === locationName)
    );
    if (found) {
      locationData = found.locationName === locationName
        ? { ...found, organization }
        : { ...found.subLocations.find((s) => s.locationName === locationName), organization };
    }
    setLocationModal({
      isOpen: true,
      title: t('shared.location_details', 'Location Details'),
      mainData: locationName,
      relatedData: [],
      loading: true,
      locationData,
    });
    try {
      const { data } = await api.get('asset-issues/organization/filtered', {
        params: { organizationLocation: locationName },
      });
      setLocationModal((prev) => ({ ...prev, relatedData: data?.response || [], loading: false }));
    } catch (e) {
      setLocationModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const requestIdCell = (tr) => (
    <span
      className="text-primary fw-medium"
      role="button"
      style={{ cursor: 'pointer' }}
      onClick={() => setEquipmentDetails({ isOpen: true, equipment: tr })}
    >
      {tr.requestId}
    </span>
  );

  const assetsCell = (tr) => {
    const withAssets = (tr.processInfo || []).filter((p) => p.linkedAsset?.id);
    if (withAssets.length === 0) return t('shared.n_a', 'N/A');
    return withAssets.map((p, i) => (
      <React.Fragment key={p.linkedAsset.id}>
        {i > 0 && ', '}
        <span
          className="text-primary"
          role="button"
          style={{ cursor: 'pointer' }}
          onClick={() => handleAssetClick(p.linkedAsset.id)}
        >
          {p.linkedAsset.name}
        </span>
      </React.Fragment>
    ));
  };

  const locationCell = (tr) => {
    const name = tr.processInfo?.[0]?.locationsInfo?.name;
    if (!name) return t('shared.n_a', 'N/A');
    return (
      <span
        className="text-primary"
        role="button"
        style={{ cursor: 'pointer' }}
        onClick={() => handleLocationClick(name)}
      >
        {name}
      </span>
    );
  };

  // Renders "Org A → Org B" for a cross-org row; within-org rows have no organization to show.
  const organizationCell = (tr) => {
    if (!tr.crossOrg) return t('shared.n_a', 'N/A');
    return (
      <span className="small">
        {tr.sourceOrganization?.name || t('shared.n_a', 'N/A')}
        <FeatherIcon icon="arrow-right" size={12} className="mx-1" />
        {tr.destinationOrganization?.name || t('shared.n_a', 'N/A')}
      </span>
    );
  };

  const typeOf = (tr) =>
    tr.processInfo?.[0]?.endDate
      ? t('business.planning.temporary_transfer', 'Temporary Transfer')
      : t('business.planning.permanent_transfer', 'Permanent Transfer');
  const reasonOf = (tr) => tr.processInfo?.[0]?.reason || '—';

  const decide = async (equipmentId, approve, rejectReason) => {
    setDeciding(equipmentId);
    try {
      await api.post('equipment/asset-transfer/decision', { equipmentId, approve, rejectReason: rejectReason || null });
      // Let the navigation drop the Planning badge right away instead of on its next poll.
      window.dispatchEvent(new Event('transfers-updated'));
      await fetchTransfers();
    } catch (e) {
      // the row simply stays pending on failure
    } finally {
      setDeciding(null);
    }
  };

  const withdraw = async (equipmentId) => {
    setDeciding(equipmentId);
    try {
      await api.post('equipment/asset-transfer/withdraw', { equipmentId });
      window.dispatchEvent(new Event('transfers-updated'));
      await fetchTransfers();
    } catch (e) {
      // the row stays pending on failure
    } finally {
      setDeciding(null);
    }
  };

  const submitReject = async () => {
    const { id, reason } = rejectModal;
    setRejectModal({ open: false, id: null, reason: '' });
    await decide(id, false, reason);
  };

  return (
    <>
      {/* Direction filter — only meaningful once this organization actually exchanges assets */}
      {(hasCrossOrgRows || direction !== 'all') && (
        <div className="d-flex gap-2 mb-3">
          {[
            { key: 'all', label: t('shared.all', 'All') },
            { key: 'outgoing', label: t('business.planning.outgoing', 'Outgoing') },
            { key: 'incoming', label: t('business.planning.incoming', 'Incoming') },
          ].map((chip) => (
            <Button
              key={chip.key}
              size="sm"
              color={direction === chip.key ? 'primary' : 'light'}
              outline={direction !== chip.key}
              onClick={() => setDirection(chip.key)}
            >
              {chip.label}
            </Button>
          ))}
        </div>
      )}

      {/* Sub-tabs (Requests | History) + the Transfer action */}
      <div className="d-flex justify-content-between align-items-end mb-3">
        <Nav tabs className="planning-subtabs border-bottom flex-grow-1">
          <NavItem>
            <NavLink
              className={classnames({ active: activeSubTab === 'requests' })}
              onClick={() => setActiveSubTab('requests')}
              style={{ cursor: 'pointer' }}
            >
              <ListChecks size={16} className="me-2" />
              {t('business.planning.pending_approval', 'Pending approval')}
              {pending.length > 0 && (
                <Badge color="warning" className="ms-2">{pending.length}</Badge>
              )}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeSubTab === 'history' })}
              onClick={() => setActiveSubTab('history')}
              style={{ cursor: 'pointer' }}
            >
              <History size={16} className="me-2" />
              {t('business.planning.history', 'History')}
            </NavLink>
          </NavItem>
        </Nav>
        {canTransfer && (
          <Button color="primary" className="ms-3 mb-2 flex-shrink-0" onClick={() => setTransferModal(true)}>
            <FeatherIcon icon="repeat" size={16} className="me-1" />
            {t('business.planning.transfer', 'Transfer')}
          </Button>
        )}
      </div>

      {/* Requests */}
      {activeSubTab === 'requests' && (
        <Card>
          <CardBody>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>{t('shared.request_id', 'Request ID')}</th>
                    <th>{t('shared.asset', 'Asset')}</th>
                    {hasCrossOrgRows && <th>{t('shared.organization', 'Organization')}</th>}
                    <th>{t('shared.location', 'Location')}</th>
                    <th>{t('shared.transfer_type', 'Transfer Type')}</th>
                    <th>{t('business.planning.requested_by', 'Requested By')}</th>
                    <th>{t('shared.reason', 'Reason')}</th>
                    {showActionsColumn && <th>{t('shared.actions', 'Actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {pending.map((tr) => (
                    <tr key={tr.id}>
                      <td>
                        {requestIdCell(tr)}
                        {stageLabel(tr) && (
                          <div className="text-muted small">{stageLabel(tr)}</div>
                        )}
                      </td>
                      <td>{assetsCell(tr)}</td>
                      {hasCrossOrgRows && <td>{organizationCell(tr)}</td>}
                      <td>{locationCell(tr)}</td>
                      <td>{typeOf(tr)}</td>
                      <td>{tr.requestedBy?.name || t('shared.n_a', 'N/A')}</td>
                      <td>{reasonOf(tr)}</td>
                      {showActionsColumn && (
                        <td>
                          <div className="d-flex gap-2">
                            {canDecide(tr) && (
                              <>
                                <Button
                                  color="success"
                                  size="sm"
                                  disabled={deciding === tr.id}
                                  onClick={() => decide(tr.id, true)}
                                >
                                  {deciding === tr.id ? <Spinner size="sm" /> : t('business.planning.approve', 'Approve')}
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  disabled={deciding === tr.id}
                                  onClick={() => setRejectModal({ open: true, id: tr.id, reason: '' })}
                                >
                                  {t('business.planning.reject', 'Reject')}
                                </Button>
                              </>
                            )}
                            {canWithdraw(tr) && (
                              <Button
                                color="secondary"
                                size="sm"
                                outline
                                disabled={deciding === tr.id}
                                onClick={() => withdraw(tr.id)}
                              >
                                {t('business.planning.withdraw', 'Withdraw')}
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && <div className="text-center py-3"><Spinner color="primary" /></div>}
              {!loading && pending.length === 0 && (
                <div className="text-center text-muted py-4">
                  {t('business.planning.no_pending_transfers', 'No transfer requests awaiting approval.')}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* History */}
      {activeSubTab === 'history' && (
        <Card>
          <CardBody>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>{t('shared.request_id', 'Request ID')}</th>
                    <th>{t('shared.asset', 'Asset')}</th>
                    {hasCrossOrgRows && <th>{t('shared.organization', 'Organization')}</th>}
                    <th>{t('shared.location', 'Location')}</th>
                    <th>{t('shared.status', 'Status')}</th>
                    <th>{t('business.planning.actioned_by', 'Actioned By')}</th>
                    <th>{t('shared.date', 'Date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((tr) => (
                    <tr key={tr.id}>
                      <td>{requestIdCell(tr)}</td>
                      <td>{assetsCell(tr)}</td>
                      {hasCrossOrgRows && <td>{organizationCell(tr)}</td>}
                      <td>{locationCell(tr)}</td>
                      <td>
                        <Badge color={statusBadgeColor(tr.status)} className="rounded-pill px-2 py-1">
                          {t(`shared.${(tr.status || '').toLowerCase()}`, (tr.status || '').replace(/_/g, ' '))}
                        </Badge>
                      </td>
                      <td>{tr.actionTakenBy?.name || t('shared.n_a', 'N/A')}</td>
                      <td>{formatCreatedDate(tr.actionDate || tr.requestDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && <div className="text-center py-3"><Spinner color="primary" /></div>}
              {!loading && log.length === 0 && (
                <div className="text-center text-muted py-4">
                  {t('business.planning.no_transfer_log', 'No transfers yet.')}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      <AssetTransferModal
        isOpen={transferModal}
        toggle={() => setTransferModal(false)}
        bulk
        organizationId={organizationId}
        onSuccess={() => fetchTransfers()}
      />

      {/* Transfer-specific details (asset(s), from→to, type, dates, reason) */}
      <TransferDetailsModal
        isOpen={equipmentDetails.isOpen}
        toggle={() => setEquipmentDetails({ isOpen: false, equipment: null })}
        transfer={equipmentDetails.equipment}
        onUpdated={(updated) => {
          if (updated) setEquipmentDetails((prev) => ({ ...prev, equipment: updated }));
          fetchTransfers();
        }}
      />

      {/* Asset details */}
      <AssetModal
        isOpen={assetModal.isOpen}
        toggle={() => setAssetModal((prev) => ({ ...prev, isOpen: false }))}
        userData={assetModal.userData}
        loading={assetModal.loading}
        formatCreatedDate={formatCreatedDate}
        onTicketClick={() => {}}
        fetchUserWorkOrders={() => {}}
      />

      {/* Location details */}
      <LocationModal
        isOpen={locationModal.isOpen}
        toggle={() => setLocationModal((prev) => ({ ...prev, isOpen: false }))}
        title={locationModal.title}
        mainData={locationModal.mainData}
        relatedData={locationModal.relatedData}
        loading={locationModal.loading}
        formatCreatedDate={formatCreatedDate}
        locationData={locationModal.locationData}
      />

      {/* Reject-reason prompt */}
      <Modal isOpen={rejectModal.open} toggle={() => setRejectModal({ open: false, id: null, reason: '' })} centered>
        <ModalHeader toggle={() => setRejectModal({ open: false, id: null, reason: '' })}>
          {t('business.planning.reject_transfer', 'Reject Transfer Request')}
        </ModalHeader>
        <ModalBody>
          <label className="form-label">{t('business.planning.reject_reason', 'Reason (optional)')}</label>
          <Input
            type="textarea"
            rows={3}
            value={rejectModal.reason}
            onChange={(e) => setRejectModal((prev) => ({ ...prev, reason: e.target.value }))}
            placeholder={t('business.planning.reject_reason_placeholder', 'Why is this transfer being rejected?')}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRejectModal({ open: false, id: null, reason: '' })}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="danger" onClick={submitReject}>
            {t('business.planning.reject', 'Reject')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TransfersTab;
